<template>
  <Dialog
    v-model:visible="visible"
    header="Unsaved Changes"
    class="max-w-sm"
    :draggable="false"
    modal
  >
    <slot name="description">
      There are unsaved changes. Do you want to save changes or discard before leaving?
    </slot>
    <template #footer>
      <Button id="discard-button" @click="discardChanges" severity="secondary"> Discard </Button>
      <Button id="save-button" @click="saveChanges" :disabled="!validToSubmit || isSaving">
        {{ isSaving ? 'Saving...' : saveButtonLabel }}
      </Button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import { ref, toRefs } from 'vue'
import {
  NavigationGuardNext,
  RouteLocationNormalized,
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
} from 'vue-router'

// eslint-disable-next-line no-unused-vars
type Resolve = (shouldProceed: boolean) => void

const visible = ref(false)
const resolve = ref<null | Resolve>(null)
/**
 * This flag prevents the dialog from appearing multiple times during a single navigation.
 *
 * When navigating to a different page with the same parameters, the navigation
 * guard can be triggered multiple times (both 'leave' and 'update' events). Without this flag,
 * the dialog would appear twice: once for the 'leave' event and again for the 'update' event.
 *
 * By setting this flag to true when the user makes a decision (discard or save), we ensure
 * that subsequent navigation guard triggers will not show the dialog again during the same
 * navigation flow.
 */
const isNavigating = ref(false)

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (e: 'discardUpdates'): void
}>()
const props = withDefaults(
  defineProps<{
    hasUpdates: boolean
    // eslint-disable-next-line no-unused-vars
    withinValidRoutes: (to: RouteLocationNormalized, from: RouteLocationNormalized) => boolean
    saveUpdates?: () => Promise<void>
    validToSubmit?: boolean
    preventNavigationOnSave?: boolean
    saveButtonLabel?: string
  }>(),
  {
    validToSubmit: true,
    saveButtonLabel: 'Save',
    saveUpdates: undefined,
    preventNavigationOnSave: false,
  },
)
const { hasUpdates, validToSubmit, preventNavigationOnSave } = toRefs(props)

function openModal(): Promise<boolean> {
  visible.value = true
  return new Promise<boolean>(res => (resolve.value = res))
}

function discardChanges() {
  visible.value = false
  emit('discardUpdates')
  isNavigating.value = true
  resolve.value?.(true)
}
const isSaving = ref(false)
async function saveChanges() {
  if (!validToSubmit.value || isSaving.value) return
  isSaving.value = true
  await props.saveUpdates?.()
  isSaving.value = false
  visible.value = false
  // If preventNavigationOnSave is true, we're keeping the user on the current page
  // so we should reset the isNavigating flag to allow future navigation guards to work
  isNavigating.value = !preventNavigationOnSave.value
  resolve.value?.(!props.preventNavigationOnSave)
}

async function preventNavigationWhenUnsavedChanges(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  if (hasUpdates.value && !props.withinValidRoutes(to, from) && !isNavigating.value) {
    const shouldProceed = await openModal()
    next(shouldProceed)
  } else next()
}
onBeforeRouteLeave(preventNavigationWhenUnsavedChanges)
onBeforeRouteUpdate(preventNavigationWhenUnsavedChanges)
</script>
